import { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  TextField,
} from '@mui/material';
import { Button } from '@ipc/ipc-components';
import CloseIcon from '@mui/icons-material/Close';

interface CommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => void;
  title?: string;
  description?: string;
  submitText?: string;
  cancelText?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  isLoading?: boolean;
}

/**
 * A reusable modal dialog for collecting comments from users
 * @param props Component props
 * @returns CommentModal component
 */
const CommentModal: FC<CommentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title = 'Add Comment',
  description = 'Please provide a comment',
  submitText = 'Submit',
  cancelText = 'Cancel',
  maxWidth = 'sm',
  fullWidth = true,
  isLoading = false,
}) => {
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset the comment and error state when the modal opens
  useEffect(() => {
    if (isOpen) {
      setComment('');
      setError(null);
    }
  }, [isOpen]);

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setComment(e.target.value);
    // Clear error when user starts typing
    if (error && e.target.value.trim()) {
      setError(null);
    }
  };

  const handleSubmit = () => {
    if (isLoading) return; // Prevent submission if already loading
    if (!comment.trim()) {
      setError('Comment is required');
      return;
    }
    onSubmit(comment);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      aria-labelledby="comment-dialog-title"
    >
      {/* Header */}
      <DialogTitle
        id="comment-dialog-title"
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          {title && (
            <Typography variant="h6" component="div">
              {title}
            </Typography>
          )}
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent dividers sx={{ p: 3 }}>
        <TextField
          autoFocus
          fullWidth
          label="Comment"
          multiline
          rows={4}
          value={comment}
          onChange={handleCommentChange}
          variant="outlined"
          error={!!error}
          helperText={error}
          placeholder="Enter your comment here..."
          InputProps={{
            sx: { 
              borderRadius: '4px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: !!error ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
              },
            },
          }}
        />
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ padding: 2, justifyContent: 'flex-end', gap: 1 }}>
        <Button 
          onClick={onClose}
          variant="secondary"
          size="small"
          disabled={isLoading}
        >
          {cancelText}
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="primary"
          size="small"
          disabled={!comment.trim() || isLoading}
        >
          {submitText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommentModal; 